import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import RentalDateRangePicker from '../components/DateRange';
import TimeSelect from '../components/TimeSelect';
import { Button, Spacer } from '@nextui-org/react';
import { BookingContext } from '../context/BookingContext';

const isJanuaryFirst = (date) => {
    if (!(date instanceof Date)) return false;
    return date.getDate() === 1 && date.getMonth() === 0;
};

const adjustToCanaryTimezone = (date) => {
    const adjustedDate = new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0, 0, 0
    ));
    return adjustedDate;
};

const calculateRentDuration = (startDate, endDate, pickupTime, returnTime) => {
    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
        return { duration: 0, extraDay: false };
    }

    // Count calendar days inclusively, ignoring time-of-day
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    const oneDayMs = 24 * 60 * 60 * 1000;
    let durationDays = Math.floor((endDay - startDay) / oneDayMs) + 1;

    if (durationDays < 1) {
        durationDays = 1;
    }

    return { duration: durationDays, extraDay: false };
};

const BookingForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setBookingData, bookingData } = useContext(BookingContext);
    const [dates, setDates] = useState({ startDate: bookingData.startDate || null, endDate: bookingData.endDate || null });
    const [pickupTime, setPickupTime] = useState(bookingData.pickupTime || '');
    const [returnTime, setReturnTime] = useState(bookingData.returnTime || '');
    const [rentDuration, setRentDuration] = useState(bookingData.rentDuration || null);
    // Deprecated: extra day message no longer used with calendar-day counting
    const [isDateInvalid, setIsDateInvalid] = useState(false);
    const [isPickupTimeInvalid, setIsPickupTimeInvalid] = useState(false);
    const [isReturnTimeInvalid, setIsReturnTimeInvalid] = useState(false);
    const [dateErrorMessage, setDateErrorMessage] = useState('');
    const [pickupTimeErrorMessage, setPickupTimeErrorMessage] = useState('');
    const [returnTimeErrorMessage, setReturnTimeErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const updateRentDuration = useCallback(() => {
        const { startDate, endDate } = dates;

        if (startDate && endDate && pickupTime && returnTime) {
            const { duration } = calculateRentDuration(new Date(startDate), new Date(endDate), pickupTime, returnTime);
            setRentDuration(duration);
            setBookingData(prevData => ({
                ...prevData,
                rentDuration: duration,
            }));
        } else {
            setRentDuration(null);
        }
    }, [dates, pickupTime, returnTime, setBookingData]);

    useEffect(() => {
        updateRentDuration();
    }, [dates, pickupTime, returnTime, updateRentDuration]);

    const handleDateChange = (ranges) => {
        const startDate = adjustToCanaryTimezone(new Date(ranges.start.toDate()));
        const endDate = adjustToCanaryTimezone(new Date(ranges.end.toDate()));

        // Блокируем получение или возврат скутера 1 января (нельзя ставить эту дату началом или окончанием аренды)
        if (isJanuaryFirst(startDate) || isJanuaryFirst(endDate)) {
            setDates({
                startDate: null,
                endDate: null
            });
            setBookingData(prevData => ({
                ...prevData,
                startDate: null,
                endDate: null,
            }));
            setIsDateInvalid(true);
            setDateErrorMessage(t('jan1_closed_error'));
            return;
        }

        setDates({
            startDate,
            endDate
        });
        setBookingData(prevData => ({
            ...prevData,
            startDate,
            endDate,
        }));
        setIsDateInvalid(false);
        setDateErrorMessage('');
    };

    const handlePickupTimeChange = (e) => {
        const time = e.target.value;
        setPickupTime(time);
        setBookingData(prevData => ({
            ...prevData,
            pickupTime: time,
        }));
        setIsPickupTimeInvalid(false);
        setPickupTimeErrorMessage('');
    };

    const handleReturnTimeChange = (e) => {
        const time = e.target.value;
        setReturnTime(time);
        setBookingData(prevData => ({
            ...prevData,
            returnTime: time,
        }));
        setIsReturnTimeInvalid(false);
        setReturnTimeErrorMessage('');
    };

    const checkAvailability = async () => {
        try {
            const response = await fetch('https://hotticketsserver-production.up.railway.app/api/check-availability', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ startDate: dates.startDate, endDate: dates.endDate })
            });

            const data = await response.json();
            setBookingData(prevData => ({
                ...prevData,
                availableSingleScooters: data.availableSingleScooters,
                availableDoubleScooters: data.availableDoubleScooters,
            }));

            if (data.availableSingleScooters === 0 && data.availableDoubleScooters === 0) {
                return false;
            }
            return true;
        } catch (error) {
            console.error('Error checking availability:', error);
            return false;
        }
    };

    const handleBookNow = async () => {
        let dateError = '';
        let pickupError = '';
        let returnError = '';

        // Дополнительная защита на случай старых сохранённых данных с 1 января
        if (dates.startDate && dates.endDate && (isJanuaryFirst(dates.startDate) || isJanuaryFirst(dates.endDate))) {
            setIsDateInvalid(true);
            dateError = t('jan1_closed_error');
        }

        if (!dates.startDate || !dates.endDate) {
            setIsDateInvalid(true);
            if (!dateError) {
                // Если уже показана ошибка про 1 января, не перезаписываем её
                if (dateErrorMessage === t('jan1_closed_error')) {
                    dateError = dateErrorMessage;
                } else {
                    dateError = t('date_error_message');
                }
            }
        }
        if (!pickupTime) {
            setIsPickupTimeInvalid(true);
            pickupError = t('pickup_time_error_message');
        }
        if (!returnTime) {
            setIsReturnTimeInvalid(true);
            returnError = t('return_time_error_message');
        }

        setDateErrorMessage(dateError);
        setPickupTimeErrorMessage(pickupError);
        setReturnTimeErrorMessage(returnError);

        // Если есть хоть одна ошибка, дальше не идём
        if (dateError || pickupError || returnError) {
            return;
        }

        if (dates.startDate && dates.endDate && pickupTime && returnTime) {
            setIsLoading(true);
            const isAvailable = await checkAvailability();

            setIsLoading(false);

            if (isAvailable) {
                navigate('/booking');
            } else {
                alert('No scooters available for the selected dates.');
                setDates({ startDate: null, endDate: null });
                setPickupTime('');
                setReturnTime('');
                setRentDuration(null);
            }
        }
    };

    return (
        <div>
            <div style={{ marginBottom: '0.5rem' }}>
              
                <RentalDateRangePicker 
                    label="Stay duration"
                    onChange={handleDateChange}
                    defaultValue={{
                      start: dates.startDate || undefined,
                      end: dates.endDate || undefined,
                  }}                  
                    isInvalid={isDateInvalid}
                    errorMessage={dateErrorMessage} 
                />
            </div>
            <div className="flex flex-row gap-2">
                <TimeSelect label={t('pickup_time')} onChange={handlePickupTimeChange} selectedKey={pickupTime} isInvalid={isPickupTimeInvalid} errorMessage={pickupTimeErrorMessage} />
                <TimeSelect label={t('return_time')} onChange={handleReturnTimeChange} selectedKey={returnTime} isInvalid={isReturnTimeInvalid} errorMessage={returnTimeErrorMessage} />
            </div>
            <Spacer y={3} />
            <Button color="primary" size="lg" fullWidth={true} onPress={handleBookNow} isLoading={isLoading}>
                {isLoading ? t('checking_availability') : (rentDuration !== null ? t('book_for', { count: rentDuration }) : t('book_now'))}
            </Button>
            {rentDuration > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                    <div style={{
                        position: 'relative',
                        display: 'inline-block',
                        margin: '0 auto',
                        padding: '8px 12px',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E4E4E7',
                        borderRadius: '10px',
                        color: '#3F3F46',
                        fontSize: '12px',
                        lineHeight: '18px',
                        textAlign: 'center',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '-7px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '7px solid transparent',
                            borderRight: '7px solid transparent',
                            borderBottom: '7px solid #E4E4E7'
                        }} />
                        <div style={{
                            position: 'absolute',
                            top: '-6px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderBottom: '6px solid #FFFFFF'
                        }} />
                        {t('calendar_day_info')}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingForm;
